import { ColoredLabel, formatDateTimeFromISO } from "widgets";

import { getCssColor, getVariantEffectColor } from "@/app-utils/color.ts";
import { generateVariantStyles } from "@/app-utils/track-shapes/shape-variant.tsx";
import type {
  AaFeature,
  Contig,
  Gene,
  NuFeature,
  Organism,
  Parent,
  Protein,
  Sample,
  Sequence,
  Transcript,
  Variant,
} from "@/graphql.js";
import { UNKNOWN_GENOME_NAME } from "@/settings/settings.js";
import type { AttributeWidgetProps } from "@/ui-modules/core/navigation-panel/AttributeWidget.js";
import { VariantSymbol } from "@/ui-modules/genome-viewer/variants-track/VariantSymbol.tsx";

export function renderSampleAttributesWidgets(sample: Sample): AttributeWidgetProps[] {
  if (!sample) return [];

  return [
    {
      label: "Status",
      description: (
        <ColoredLabel
          color={sample.status.color ? getCssColor(sample.status.color) : undefined}
          label={parent ? sample.status.name : "not defined"}
        />
      ),
    },
    { label: "Added", description: formatDateTimeFromISO(sample.created) },
    { label: "Date Modified", description: formatDateTimeFromISO(sample.modified) },
  ];
}

export function renderGenomeAttributesWidgets(genome?: Organism): AttributeWidgetProps[] {
  if (!genome) return [];

  return [
    { label: "Species", description: genome.species?.name },
    { label: "Genome Type", description: genome.sequencingType },
  ];
}

export function renderAaFeatureAttributesWidgets(aaFeature: AaFeature): AttributeWidgetProps[] {
  if (!aaFeature?.genome) return [];

  return [
    { label: "Genome", description: aaFeature.genome.name, linkTo: `/organism/${aaFeature.genome.id}` },
    { label: "Species", description: aaFeature.genome.species.name },
  ];
}

export function renderContigAttributesWidgets(contig: Contig): AttributeWidgetProps[] {
  if (!contig) return [];

  return [
    { label: "Contig name", description: contig.name },
    contig.organism.name !== UNKNOWN_GENOME_NAME
      ? { label: "Genome", description: contig.organism.name, linkTo: `/organism/${contig.organism.id}` }
      : { label: "Database", description: contig.database.mnemonic },
  ];
}

export function renderGeneAttributesWidgets(gene: Gene): AttributeWidgetProps[] {
  if (!gene) return [];

  return [
    { label: "Gene type", description: gene.geneType },
    gene.genome.name !== UNKNOWN_GENOME_NAME
      ? { label: "Genome", description: gene.genome.name, linkTo: `/organism/${gene.genome.id}` }
      : { label: "Database", description: gene.database.mnemonic },
    { label: "Species", description: gene.genome.species.name },
    gene.synonyms && gene.synonyms.length > 0 && { label: "Synonyms", description: gene.synonyms.join(", ") },
  ];
}

export function renderNuFeatureAttributesWidgets(nuFeature: NuFeature): AttributeWidgetProps[] {
  if (!nuFeature?.genome) return [];

  return [
    { label: "Genome", description: nuFeature.genome.name, linkTo: `/organism/${nuFeature.genome.id}` },
    { label: "Species", description: nuFeature.genome.species.name },
  ];
}

export function renderProteinAttributesWidgets(protein: Protein): AttributeWidgetProps[] {
  if (!protein) return [];

  return [
    { label: "Systematic name", description: protein.systematicName },
    protein.genome.name !== UNKNOWN_GENOME_NAME
      ? { label: "Genome", description: protein.genome.name, linkTo: `/organism/${protein.genome.id}` }
      : { label: "Database", description: protein.database.mnemonic },
    { label: "Species", description: protein.genome.species.name },
  ];
}

export function renderSampleParentAttributesWidgets(parent: Parent): AttributeWidgetProps[] {
  if (!parent) return [];

  return [
    {
      label: "Status",
      description: (
        <ColoredLabel
          color={parent.status?.color ? getCssColor(parent.status.color) : undefined}
          label={parent.status?.name ?? "not defined"}
        />
      ),
    },
  ];
}

export function renderSequenceAttributesWidgets(sequence: Sequence): AttributeWidgetProps[] {
  if (!sequence) return [];

  return [
    { label: "Sequence type", description: sequence.sequenceType },
    { label: "Sequence name", description: sequence.name },
    { label: "Full ID", description: `${sequence.database.mnemonic}:${sequence.sequenceId}` },
  ];
}

export function renderTranscriptAttributesWidgets(transcript: Transcript): AttributeWidgetProps[] {
  if (!transcript) return [];

  const speciesName = transcript.genome.id !== 0 ? transcript.genome.species.name : transcript.species.name;

  return [
    { label: "Transcript name", description: transcript.name },
    transcript.genome.name !== UNKNOWN_GENOME_NAME
      ? { label: "Genome", description: transcript.genome.name, linkTo: `/organism/${transcript.genome.id}` }
      : { label: "Database", description: transcript.database.mnemonic },
    {
      label: "Species",
      description: speciesName,
    },
  ];
}

export function renderVariantAttributesWidgets(variant: Variant): AttributeWidgetProps[] {
  if (!variant) return [];

  return [
    {
      label: "Effect",
      description: <ColoredLabel color={getVariantEffectColor(variant.effect)}>{variant.effect}</ColoredLabel>,
    },
    {
      label: "Type",
      description: (
        <span>
          <VariantSymbol
            styleContent={generateVariantStyles(variant.variantType).symbolElementStyle}
            type={variant.variantType}
            width={12}
          />{" "}
          <span>{variant.variantType}</span>
        </span>
      ),
    },
    { label: "Reference genome", description: variant.refGenome.name, linkTo: `/organism/${variant.refGenome.id}` },
    { label: "Genome analysis group", description: variant.variantAnalysisGroup.name },
  ];
}
